import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-y-4 text-left",
  "data-testid": "send-record-admin-adjustment"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "flex items-center justify-between gap-2" }, [
  /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-bold text-textPrimary" }, "Ajuste administrativo"),
  /*#__PURE__*/_createElementVNode("span", { class: "text-xs font-bold rounded-full bg-gray-100 text-gray-700 px-2 py-0.5" }, " Somente auditoria ")
], -1)
const _hoisted_3 = { class: "rounded-lg border border-gray-200 bg-gray-50 px-3 py-2" }
const _hoisted_4 = { class: "text-sm text-textPrimary" }
const _hoisted_5 = { class: "text-xs text-neutral50" }
const _hoisted_6 = { class: "flex flex-col text-xs text-neutral50 max-w-[220px]" }
const _hoisted_7 = ["value", "data-testid"]
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "flex flex-wrap items-end gap-3" }
const _hoisted_10 = { class: "flex flex-col text-xs text-neutral50" }
const _hoisted_11 = ["value", "disabled", "data-testid"]
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { class: "flex flex-col text-xs text-neutral50" }
const _hoisted_14 = ["value", "disabled", "data-testid"]
const _hoisted_15 = { key: 0 }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("label", {
  class: "text-sm font-bold text-textPrimary",
  for: "send-record-admin-justification"
}, " Justificativa * ", -1)
const _hoisted_17 = ["value"]
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", { class: "rounded bg-primary5/40 border border-primary30 px-3 py-2 text-xs text-textPrimary" }, " Este ajuste altera o horário efetivo do plantão. Fica registrado como fonte \"Registro de Atividade\", com o seu nome e a justificativa, e precisa ser aprovado por outra pessoa. ", -1)

import { computed } from 'vue'
import {
  CoveredLegs,
  COVERED_LEGS_LABELS,
  ACTIVITY_DESCRIPTION_MIN_LENGTH
} from '@/constants/ds-attendance.constants'
import {
  applyTimeInputToIso,
  isDescriptionLongEnough,
  isoToTimeInput,
  type NormalizedPrefillItem,
  type SendRecordLine
} from './types'

/**
 * Variação compacta de M2-2. Com o bloqueio TOTAL da inserção manual de
 * check-in/out (Q4/Q18), esta é a ÚNICA porta para a auditoria corrigir um
 * horário: um plantão, uma justificativa, um clique. O anexo é opcional (V7b) —
 * aqui o documento é a justificativa assinada pela auditoria.
 */


export default /*#__PURE__*/_defineComponent({
  __name: 'AdminAdjustmentForm',
  props: {
    item: {},
    line: {},
    description: {}
  },
  emits: ["update:line", "update:description"],
  setup(__props: any, { emit }) {

const props = __props;





const coveredLegsOptions = Object.values(CoveredLegs)
const minLength = ACTIVITY_DESCRIPTION_MIN_LENGTH

const isLongEnough = computed(() => isDescriptionLongEnough(props.description))

const contextLine = computed(() => {
  const item = props.item
  if (!item) return ''
  const hours =
    item.expectedStart && item.expectedEnd
      ? `${isoToTimeInput(item.expectedStart)}–${isoToTimeInput(item.expectedEnd)}`
      : ''
  return [item.healthUnitName, item.scheduleName, formatDate(item.expectedStart), hours]
    .filter(Boolean)
    .join(' · ')
})

function covers(leg: CoveredLegs): boolean {
  return props.line.coveredLegs === leg || props.line.coveredLegs === CoveredLegs.BOTH
}

function updateLeg(event: Event): void {
  emit('update:line', {
    ...props.line,
    coveredLegs: (event.target as HTMLSelectElement).value as CoveredLegs
  })
}

function updateTime(field: 'declaredCheckin' | 'declaredCheckout', event: Event): void {
  const base = field === 'declaredCheckin' ? props.item?.expectedStart : props.item?.expectedEnd
  emit('update:line', {
    ...props.line,
    [field]: applyTimeInputToIso(
      base ?? props.line[field],
      (event.target as HTMLInputElement).value
    )
  })
}

function formatDate(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('pt-BR')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.item?.doctorName || 'Plantão selecionado'), 1),
      _createElementVNode("p", _hoisted_5, _toDisplayString(contextLine.value), 1)
    ]),
    _createElementVNode("label", _hoisted_6, [
      _createTextVNode(" Equivale a * "),
      _createElementVNode("select", {
        class: "rounded border border-gray-200 px-2 py-1 text-sm text-textPrimary",
        value: _ctx.line.coveredLegs,
        "data-testid": `send-record-leg-select-${_ctx.line.doctorStationId}`,
        onChange: _cache[0] || (_cache[0] = ($event: any) => (updateLeg($event)))
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(coveredLegsOptions), (legs) => {
          return (_openBlock(), _createElementBlock("option", {
            key: legs,
            value: legs
          }, _toDisplayString(_unref(COVERED_LEGS_LABELS)[legs]), 9, _hoisted_8))
        }), 128))
      ], 40, _hoisted_7)
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("label", _hoisted_10, [
        _createTextVNode(" Check-in declarado "),
        _createElementVNode("input", {
          type: "time",
          class: "rounded border border-gray-200 px-2 py-1 text-sm text-textPrimary",
          value: _unref(isoToTimeInput)(_ctx.line.declaredCheckin),
          disabled: !covers(_unref(CoveredLegs).CHECKIN),
          "data-testid": `send-record-checkin-${_ctx.line.doctorStationId}`,
          onInput: _cache[1] || (_cache[1] = ($event: any) => (updateTime('declaredCheckin', $event)))
        }, null, 40, _hoisted_11),
        (_ctx.item?.expectedStart)
          ? (_openBlock(), _createElementBlock("span", _hoisted_12, "previsto " + _toDisplayString(_unref(isoToTimeInput)(_ctx.item.expectedStart)), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("label", _hoisted_13, [
        _createTextVNode(" Check-out declarado "),
        _createElementVNode("input", {
          type: "time",
          class: "rounded border border-gray-200 px-2 py-1 text-sm text-textPrimary",
          value: _unref(isoToTimeInput)(_ctx.line.declaredCheckout),
          disabled: !covers(_unref(CoveredLegs).CHECKOUT),
          "data-testid": `send-record-checkout-${_ctx.line.doctorStationId}`,
          onInput: _cache[2] || (_cache[2] = ($event: any) => (updateTime('declaredCheckout', $event)))
        }, null, 40, _hoisted_14),
        (_ctx.item?.expectedEnd)
          ? (_openBlock(), _createElementBlock("span", _hoisted_15, "previsto " + _toDisplayString(_unref(isoToTimeInput)(_ctx.item.expectedEnd)), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", null, [
      _hoisted_16,
      _createElementVNode("textarea", {
        id: "send-record-admin-justification",
        "data-testid": "send-record-admin-justification",
        rows: "3",
        maxlength: "500",
        class: "w-full rounded border border-gray-200 px-2 py-1 text-sm",
        value: _ctx.description,
        onInput: _cache[3] || (_cache[3] = ($event: any) => (emit('update:description', ($event.target as HTMLTextAreaElement).value)))
      }, null, 40, _hoisted_17),
      _createElementVNode("p", {
        class: _normalizeClass(["text-xs", isLongEnough.value ? 'text-neutral50' : 'text-red-700'])
      }, " Obrigatória, mínimo " + _toDisplayString(_unref(minLength)) + " caracteres · " + _toDisplayString(_ctx.description.trim().length) + "/500 · fica no histórico do plantão com o seu nome. ", 3)
    ]),
    _hoisted_18
  ]))
}
}

})